/**
 * API client for the INVI HQ Insights dashboard. Interfaces mirror the backend
 * Pydantic schemas in backend/schemas.py (InsightsSummary and friends).
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

export interface InsightsToolUsage {
  tool: string
  label: string
  count: number
}

export interface InsightsActiveUser {
  user_id: number
  name: string
  username: string
  dealer_group: string | null
  run_count: number
}

export interface InsightsError {
  tool: string
  label: string
  message: string | null
  dealer_group: string | null
  occurred_at: string
}

export interface InsightsTimePoint {
  date: string
  counts: Record<string, number>
}

export interface InsightsPageView {
  path: string
  count: number
}

export interface InsightsUserUtility {
  user_id: number
  name: string
  username: string
  dealer_group: string | null
  is_headquarters: boolean
  last_active_at: string | null
  runs: number
  page_views: number
  errors: number
}

export interface InsightsSummary {
  dealer_group_id: number | null
  days: number
  totals: Record<string, number>
  tool_usage: InsightsToolUsage[]
  outcomes: Record<string, number>
  most_active_users: InsightsActiveUser[]
  most_visited_pages: InsightsPageView[]
  recent_errors: InsightsError[]
  timeseries: InsightsTimePoint[]
}

export interface InsightsEventLogEntry {
  event_type: string
  path: string | null
  status_code: number | null
  message: string | null
  occurred_at: string
}

export interface InsightsErrorRate {
  tool: string
  label: string
  uses: number
  errors: number
  rate: number
}

export interface InsightsDisengagedUser {
  user_id: number
  name: string
  username: string
  dealer_group: string | null
  last_active: string | null
  days_inactive: number | null
}

export interface InsightsFeedFreshness {
  vendor_name: string
  dealer_group: string | null
  last_success_at: string | null
  last_run_at: string | null
  is_stale: boolean
  last_error_message: string | null
}

export interface InsightsHealthScore {
  dealer_group_id: number
  dealer_group: string
  score: number
  status: string
  active_users: number
  error_rate: number
  stale_feeds: number
}

export interface InsightsHealth {
  error_rates: InsightsErrorRate[]
  disengaged_users: InsightsDisengagedUser[]
  ingestion_freshness: InsightsFeedFreshness[]
  scorecard: InsightsHealthScore[]
}

/**
 * The analytics window: either a last-N-days preset (`days`) or a custom
 * `startDate`/`endDate` pair (YYYY-MM-DD). When both dates are set they win.
 */
export interface InsightsRange {
  days?: number
  startDate?: string
  endDate?: string
}

function rangeParams(range: InsightsRange): URLSearchParams {
  const params = new URLSearchParams()
  if (range.startDate && range.endDate) {
    params.set("start_date", range.startDate)
    params.set("end_date", range.endDate)
  } else if (range.days != null) {
    params.set("days", String(range.days))
  }
  return params
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem(TOKEN_KEY)
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const data = await res.json().catch(() => null)
    throw new Error(data?.detail || `Request failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

export async function fetchInsightsSummary(
  range: InsightsRange,
  dealerGroupId: number | null,
): Promise<InsightsSummary> {
  const params = rangeParams(range)
  if (dealerGroupId !== null) params.set("dealer_group_id", String(dealerGroupId))
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.insights.summary}?${params.toString()}`,
    { headers: authHeaders() },
  )
  return handleResponse<InsightsSummary>(res)
}

export async function fetchInsightsUsers(
  range: InsightsRange,
  dealerGroupId: number | null,
): Promise<InsightsUserUtility[]> {
  const params = rangeParams(range)
  if (dealerGroupId !== null) params.set("dealer_group_id", String(dealerGroupId))
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.insights.users}?${params.toString()}`,
    { headers: authHeaders() },
  )
  return handleResponse<InsightsUserUtility[]>(res)
}

export async function fetchUserEvents(
  userId: number,
  range: InsightsRange,
): Promise<InsightsEventLogEntry[]> {
  const params = rangeParams(range)
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.insights.users}/${userId}/events?${params.toString()}`,
    { headers: authHeaders() },
  )
  return handleResponse<InsightsEventLogEntry[]>(res)
}

export async function fetchInsightsHealth(
  range: InsightsRange,
  dealerGroupId: number | null,
): Promise<InsightsHealth> {
  const params = rangeParams(range)
  if (dealerGroupId !== null) params.set("dealer_group_id", String(dealerGroupId))
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.insights.health}?${params.toString()}`,
    { headers: authHeaders() },
  )
  return handleResponse<InsightsHealth>(res)
}

export async function sendDigestPreview(): Promise<{ sent: boolean; recipient: string }> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.insights.digestPreview}`, {
    method: "POST",
    headers: authHeaders(),
  })
  return handleResponse<{ sent: boolean; recipient: string }>(res)
}
